import React from "react";
import * as Yup from "yup";
import { graphql } from "babel-plugin-relay/macro";
import { commitMutation, Environment, useFragment, useMutation } from "react-relay";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import {
	RenderConfig,
	ValidatedField,
} from "../../../../../../../../infecto-lms-admin/components/core/form/ValidatedField";
import { TreeConfigurationEditDialogProps } from "../../../../../../../../infecto-lms-admin/components/tree/editor/node/config/components/Config.interfaces";
import { MultiSelect } from "primereact/multiselect";
import { ConfigDatePicker } from "../../../../../../../../infecto-lms-admin/components/tree/editor/node/config/components/ConfigDatePicker";
import { CONFIGS_TRANSLATIONS } from "../../../../../../../../infecto-lms-admin/i18n/config/i18n";
import {
	AddSpecificUserStatusesCanStartContentConfigInput,
	SpecificUserStatusesCanStartContentConfigForm_AddMutation,
} from "../../../../../../../../__generated__/SpecificUserStatusesCanStartContentConfigForm_AddMutation.graphql";
import { SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment$key } from "../../../../../../../../__generated__/SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment.graphql";
import {
	SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment$key,
	UserStatus,
} from "../../../../../../../../__generated__/SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment.graphql";
import { SpecificUserStatusesCanStartContentConfigForm_EditMutation } from "../../../../../../../../__generated__/SpecificUserStatusesCanStartContentConfigForm_EditMutation.graphql";
import { GLOBAL_USER_STATUS_CONTENT_TRANSLATIONS } from "../../../../../../../../infecto-lms-admin/i18n/user-statuu-types";

const CONFIGURATION_FRAGMENT = graphql`
	fragment SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment on StartContentConfig {
		... on SpecifiedUserStatusesCanStartContentConfig {
			id
			configType
			dateTime
			requiredUserStatuses
		}
	}
`;

const TREE_NODE_FRAGMENT = graphql`
	fragment SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment on TreeNode {
		id
		typeDefinition {
			... on ELearningContentTypeDefinition {
				startConfigs {
					...SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment
				}
			}
		}
	}
`;

const ADD_CONFIGURATION_MUTATION = graphql`
	mutation SpecificUserStatusesCanStartContentConfigForm_AddMutation(
		$input: AddSpecificUserStatusesCanStartContentConfigInput!
	) {
		Admin {
			Tree {
				addSpecificUserStatusesCanStartContentConfig(input: $input) {
					contentNode {
						...SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment
					}
				}
			}
		}
	}
`;

const EDIT_CONFIGURATION_MUTATION = graphql`
	mutation SpecificUserStatusesCanStartContentConfigForm_EditMutation(
		$input: EditSpecificUserStatusesCanStartContentConfigInput!
	) {
		Admin {
			Tree {
				EditSpecificUserStatusesCanStartContentConfig(input: $input) {
					contentNode {
						...SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment
					}
				}
			}
		}
	}
`;

function CommitAddSpecificUserStatusesCanStartContentConfig(
	environment: Environment,
	input: AddSpecificUserStatusesCanStartContentConfigInput,
) {
	return commitMutation<SpecificUserStatusesCanStartContentConfigForm_AddMutation>(environment, {
		mutation: ADD_CONFIGURATION_MUTATION,
		variables: { input },
		onCompleted: () => {},
		onError: () => {},
	});
}

interface FormState {
	date: string;
	requiredUserStatuses: UserStatus[];
}

interface Props extends TreeConfigurationEditDialogProps {
	treeNodeFragmentRef: SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment$key;
	configurationFragmentRef: SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment$key;
	onBack?: () => void;
}

export const SpecificUserStatusesCanStartContentConfigForm = ({
	configurationFragmentRef,
	treeNodeFragmentRef,
	onBack,
}: Props) => {
	const treeNode =
		useFragment<SpecificUserStatusesCanStartContentConfigForm_TreeNodeFragment$key>(
			TREE_NODE_FRAGMENT,
			treeNodeFragmentRef,
		);
	const configuration =
		useFragment<SpecificUserStatusesCanStartContentConfigForm_StartContentConfigFragment$key>(
			CONFIGURATION_FRAGMENT,
			configurationFragmentRef,
		);
	const [edit, isEditing] =
		useMutation<SpecificUserStatusesCanStartContentConfigForm_EditMutation>(
			EDIT_CONFIGURATION_MUTATION,
		);

	const formik = useFormik<FormState>({
		initialValues: {
			date: configuration.dateTime as string,
			requiredUserStatuses: [
				...(configuration.requiredUserStatuses?.filter(
					(e: any) => e !== "noProcess",
				) as UserStatus[]),
			],
		},
		validationSchema: Yup.object().shape({
			requiredUserStatuses: Yup.array().required("Das Feld Benötigte Stati wird benötigt."),
		}),
		onSubmit: (values, formikHelpers) => {
			edit({
				variables: {
					input: {
						contentNodeId: treeNode.id,
						configId: configuration.id as string,
						newDateTime: values.date,
						newUserStatuses: values.requiredUserStatuses,
					},
				},
				onCompleted: () => {
					formikHelpers.setTouched({});
					onBack!();
				},
			});
		},
	});

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<FormState, UserStatus[]>
				name={"requiredUserStatuses"}
				label={CONFIGS_TRANSLATIONS[configuration.configType!]}
				component={({ fieldName, updateField, fieldValue }) => {
					return (
						<MultiSelect
							name={fieldName}
							value={fieldValue}
							onChange={(e) => {
								updateField(e.value);
							}}
							options={Object.entries(GLOBAL_USER_STATUS_CONTENT_TRANSLATIONS).map(
								(x) => {
									return {
										value: x[0],
										label: x[1],
									};
								},
							)}
						/>
					);
				}}
				formikConfig={formik}
			/>

			<ValidatedField<FormState, string>
				name={"date"}
				label={"Datum"}
				className="mb-4"
				formikConfig={formik}
				component={({
					fieldName,
					fieldValue,
					updateField,
					disabled,
				}: RenderConfig<string>) => {
					return (
						<ConfigDatePicker
							name={fieldName}
							value={fieldValue}
							onUpdate={updateField}
							disabled={disabled}
							isEndOfTheDay={true}
						/>
					);
				}}
			/>

			<Button
				disabled={Object.entries(formik.touched).length === 0 || isEditing}
				type="submit"
				label="Speichern"
				className="mt-2"
			/>

			<Button
				type="button"
				onClick={() => {
					// formik.resetForm();
					onBack!();
				}}
				label="Zurück"
				className="p-button-secondary mt-2"
			/>
		</form>
	);
};

export { CommitAddSpecificUserStatusesCanStartContentConfig as AddSpecificUserStatusesCanStartContentConfigMutation };
